<template>
  <section
    v-if="variant === 'default'"
    class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800/40 dark:border-border-inverse/10 flex flex-col items-center justify-center px-6 py-12 relative rounded-xl sm:px-12 text-center transition-all w-full"
    :class="[containerClass, { 'min-h-[60vh]': props.fullHeight }]"
  >
    <div
      class="bg-surface-level-100 dark:bg-surface-level-800 dark:ring-focus-level-800/50 flex h-16 items-center justify-center mb-4 ring-8 ring-focus-level-50 rounded-full w-16"
    >
      <slot name="icon">
        <component
          :is="currentVariant.icon"
          class="dark:text-content-level-500 h-8 text-content-level-400 w-8"
        />
      </slot>
    </div>

    <h3 class="dark:text-content-inverse font-semibold mt-2 text-content-level-900 text-lg">
      {{ title || currentVariant.defaultTitle }}
    </h3>

    <p class="dark:text-content-level-400 max-w-sm mt-2 text-content-level-500 text-sm">
      <slot name="description">
        {{ resolvedVariantDescription }}
      </slot>
    </p>

    <div
      v-if="showAction || $slots['actions']"
      class="mt-6"
    >
      <slot name="actions">
        <button
          type="button"
          class="active:scale-95 bg-primary cursor-pointer dark:focus:ring-offset-focus-level-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-medium gap-2 hover:bg-primary/90 hover:shadow-md inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-content-inverse text-sm transition-all"
          @click="handleAction"
        >
          <slot name="actionIcon" />
          {{ resolvedActionLabel || currentVariant.defaultActionLabel }}
        </button>
      </slot>
    </div>
  </section>

  <section
    v-else
    class="backdrop-blur-sm bg-[image:var(--surface-empty-state-emphasis)] border border-border-level-200/60 dark:bg-[image:var(--surface-empty-state-emphasis-dark)] dark:border-border-inverse/10 duration-300 overflow-clip p-8 relative rounded-2xl sm:p-12 text-center transition-all"
    :class="[containerClass]"
  >
    <div
      class="-left-24 -top-24 absolute bg-primary/10 blur-3xl pointer-events-none rounded-full size-48"
    />

    <div
      class="-bottom-24 -right-24 absolute bg-primary/10 blur-3xl pointer-events-none rounded-full size-48"
    />

    <div class="relative z-(--z-index-ds-raised)">
      <div
        class="bg-surface-canvas border border-border-level-200 dark:bg-surface-level-800 dark:border-border-inverse/10 dark:ring-focus-inverse/5 flex items-center justify-center mb-6 mx-auto ring-8 ring-focus-level-50/50 rounded-2xl shadow-sm size-20"
      >
        <slot name="icon">
          <div class="flex items-center justify-center relative">
            <div class="absolute animate-ping bg-primary/5 inset-0 opacity-20 rounded-full" />

            <component
              :is="currentVariant.icon"
              class="dark:text-content-level-500 size-9 text-content-level-400"
            />
          </div>
        </slot>
      </div>

      <h3 class="dark:text-content-inverse font-bold text-content-level-900 text-xl tracking-tight">
        {{ title || currentVariant.defaultTitle }}
      </h3>

      <p
        class="dark:text-content-level-400 leading-relaxed max-w-sm mt-3 mx-auto text-balance text-content-level-500 text-sm"
      >
        <slot name="description">
          {{ resolvedVariantDescription }}
        </slot>
      </p>

      <div
        v-if="showAction || $slots['actions']"
        class="flex gap-3 items-center justify-center mt-8"
      >
        <slot name="actions">
          <button
            type="button"
            class="active:scale-95 bg-surface-canvas border border-border-level-300 cursor-pointer dark:bg-surface-level-800 dark:border-border-inverse/10 dark:hover:bg-surface-canvas/5 dark:text-content-level-200 font-semibold hover:bg-surface-level-50 hover:border-border-level-400 inline-flex items-center justify-center px-5 py-2.5 rounded-xl shadow-sm text-content-level-700 text-sm transition-all"
            @click="handleAction"
          >
            {{ resolvedActionLabel || currentVariant.defaultActionLabel }}
          </button>
        </slot>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { SearchX, FilterX, AlertCircle, PlusCircle, FileSearch, Eraser } from '@lucide/vue'

type Variant = 'default' | 'search' | 'filter' | 'empty' | 'error' | 'create'

interface Props {
  /** Visual and behavioral empty-state variant. */
  variant?: Variant
  /** Optional heading displayed above the empty-state description. */
  title?: string
  /** Optional custom description text. */
  description?: string
  /** Search term used to build the default search description. */
  searchTerm?: string
  /** Human-readable resource name used in generated copy. */
  resourceLabel?: string
  /** Custom action label, or null to use the variant default. */
  actionLabel?: string | null
  /** Whether to render the primary action. */
  showAction?: boolean
  /** Whether the empty state should fill its available height. */
  fullHeight?: boolean
  /** Additional classes applied to the container. */
  containerClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  actionLabel: null,
  containerClass: '',
  description: '',
  fullHeight: true,
  resourceLabel: 'items',
  searchTerm: '',
  title: '',
  variant: 'default'
})

const emit = defineEmits<(_event: 'action') => void>()

defineSlots<{
  actions?: () => unknown
  actionIcon?: () => unknown
  description?: () => unknown
  icon?: () => unknown
}>()

const variantConfig = {
  create: {
    defaultActionLabel: 'Get started',
    defaultTitle: 'Create your first item',
    icon: PlusCircle,
    subIcon: null
  },
  default: {
    defaultActionLabel: 'Try again',
    defaultTitle: 'No results found',
    icon: SearchX,
    subIcon: null
  },
  empty: {
    defaultActionLabel: 'Add first item',
    defaultTitle: 'No data available',
    icon: SearchX,
    subIcon: null
  },
  error: {
    defaultActionLabel: 'Try again',
    defaultTitle: 'Something went wrong',
    icon: AlertCircle,
    subIcon: null
  },
  filter: {
    defaultActionLabel: 'Reset filters',
    defaultTitle: 'No results for these filters',
    icon: FilterX,
    subIcon: Eraser
  },
  search: {
    defaultActionLabel: 'Clear search',
    defaultTitle: 'No matches found',
    icon: FileSearch,
    subIcon: SearchX
  }
} as const

const currentVariant = computed(() => variantConfig[props.variant])
const resolvedActionLabel = computed(() => props.actionLabel)

const handleAction = () => {
  emit('action')
}

const resolvedVariantDescription = computed(() => {
  if (props.description) return props.description

  const resource = props.resourceLabel.toLowerCase()

  if (props.variant === 'search') {
    return props.searchTerm.trim()
      ? `We couldn't find "${props.searchTerm}" in your ${resource}. Try a different keyword.`
      : `No ${resource} matched your search.`
  }

  if (props.variant === 'filter') {
    return `No ${resource} match the active filters. Try adjusting them.`
  }

  if (props.variant === 'error') {
    return 'There was an error loading this information. Please check your connection.'
  }

  return `You do not have any ${resource} in this section yet.`
})
</script>
